import { MapPin } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useWatch } from "react-hook-form";

import {
  AddressFields,
  CheckboxField,
  DateField,
  FormRow,
  FormSheet,
  TextareaField
} from "@/components/form";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { ForeignNationalAddressHistoryEntry } from "../../../../api/types/foreignNationalAddressHistory";
import { useForeignNationals } from "../../../foreignNationals/context/ForeignNationalsContext";
import { CoverageBar, GapRow } from "../../components/CoverageBar";
import {
  AgentReadCard,
  FillFromDocuments
} from "../../components/FillFromDocuments";
import { SourceCell } from "../../components/SourceTag";
import {
  ClientTable,
  type ClientTableColumn
} from "../../components/table/ClientTable";
import { ClientTableRow } from "../../components/table/ClientTableRow";
import { ClientTableToolbar } from "../../components/table/ClientTableToolbar";
import {
  CheckCell,
  DateRangeCell,
  MutedCell,
  SkeletonRow,
  TitleCell
} from "../../components/table/cells";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { useHistoryCoverage } from "../../hooks/useHistoryCoverage";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import { addressFromRaw } from "../../lib/address";
import { dateRangeLabel, mostRecentCurrent } from "../../lib/records";
import { NAV_ITEMS } from "../../navConfig";
import {
  addressDraftSchema,
  addressDraftToPayload,
  addressEntryToDraft,
  addressFocusDomId,
  emptyAddressDraft,
  sortAddressEntries,
  type AddressDraft,
  type AddressPayload
} from "./addressesModel";

const getId = (e: ForeignNationalAddressHistoryEntry) => e.address_history_id;

const COLUMNS: ClientTableColumn[] = [
  { id: "address", header: "Address" },
  { id: "city", header: "City / State / Zip" },
  { id: "country", header: "Country" },
  { id: "dates", header: "Dates" },
  { id: "current", header: "Current", className: "w-30" },
  { id: "source", header: "Source", className: "w-30" }
];

const periodOf = (row: { entry: ForeignNationalAddressHistoryEntry }) => ({
  start: row.entry.start_date,
  end: row.entry.end_date,
  current: Boolean(row.entry.is_current)
});

const cityStateZip = (a: ReturnType<typeof addressFromRaw>): string =>
  [[a.city, a.state].filter(Boolean).join(", "), a.zipCode]
    .filter(Boolean)
    .join(" ");

/** Address history: list of residences with a Sheet editor; keeps `physical_address` in sync. */
export const AddressesTab: FC = () => {
  const { clientId, url } = useClientProfile();
  const { updateForeignNational } = useForeignNationals();
  const [search, setSearch] = useState("");

  useEffect(() => setSearch(""), [clientId]);

  // The client's physical address mirrors the most recent "current" entry.
  const syncPhysicalAddress = useCallback(
    async (entries: ForeignNationalAddressHistoryEntry[]) => {
      const current = mostRecentCurrent(
        entries,
        (e) => e.start_date,
        (e) => Boolean(e.is_current)
      );
      try {
        await updateForeignNational(clientId, {
          physical_address: current?.address ?? null
        });
      } catch {
        // The context already toasts; the history save itself succeeded.
      }
    },
    [clientId, updateForeignNational]
  );

  const crud = useRecordCrud<
    ForeignNationalAddressHistoryEntry,
    AddressDraft,
    AddressPayload
  >({
    clientId,
    noun: "Address",
    list: ForeignNationalsAPI.listAddressHistoryEntries,
    create: ForeignNationalsAPI.createAddressHistoryEntry,
    update: ForeignNationalsAPI.updateAddressHistoryEntry,
    remove: ForeignNationalsAPI.deleteAddressHistoryEntry,
    getId,
    toDraft: addressEntryToDraft,
    emptyDraft: emptyAddressDraft,
    toPayload: addressDraftToPayload,
    schema: addressDraftSchema,
    sort: sortAddressEntries,
    afterMutate: syncPhysicalAddress
  });

  const { highlightedId } = useFocusScroll(
    url.state.focus.addressHistoryId
      ? addressFocusDomId(url.state.focus.addressHistoryId)
      : null,
    !crud.isLoading && crud.records.length > 0
  );

  const rows = useMemo(() => {
    const all = crud.records.map((e) => {
      const a = addressFromRaw(e.address);
      const dates = dateRangeLabel(e.start_date, e.end_date, e.is_current);
      return {
        entry: e,
        id: getId(e),
        domId: addressFocusDomId(getId(e)),
        street: a.street || "—",
        cityStateZip: cityStateZip(a) || "—",
        country: a.country || "—",
        dates,
        isCurrent: Boolean(e.is_current),
        haystack: `${a.street} ${a.city} ${a.state} ${a.zipCode} ${
          a.country
        } ${dates} ${e.notes ?? ""}`.toLowerCase()
      };
    });
    const q = search.trim().toLowerCase();
    return q ? all.filter((r) => r.haystack.includes(q)) : all;
  }, [crud.records, search]);

  const searching = search.trim().length > 0;
  const { coverage, items } = useHistoryCoverage(
    "address",
    rows,
    periodOf,
    !crud.isLoading && !searching && crud.records.length > 0
  );

  const { form } = crud;
  const isEditing = crud.editor.mode === "edit";
  const isCurrent = useWatch({ control: form.control, name: "isCurrent" });

  // A current address has no end date.
  useEffect(() => {
    if (isCurrent && form.getValues("endDate")) {
      form.setValue("endDate", "", { shouldDirty: true, shouldValidate: true });
    }
  }, [form, isCurrent]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.address_history.label}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search addresses...",
          ariaLabel: "Search addresses"
        }}
        onRefresh={() => crud.refresh().catch(() => undefined)}
        isRefreshing={crud.isLoading}
        secondaryActions={<FillFromDocuments />}
        primaryAction={{
          label: "Add Address",
          onClick: () => crud.openCreate()
        }}
      />
      <CoverageBar kind="address" coverage={coverage} />
      <ClientTable
        ariaLabel="Address history"
        columns={COLUMNS}
        isLoading={crud.isLoading}
        isEmpty={rows.length === 0}
        error={crud.error}
        onRetry={() => crud.refresh().catch(() => undefined)}
        renderSkeletonRow={(i) => (
          <SkeletonRow
            key={i}
            widths={["w-48", "w-32", "w-24", "w-32", "w-12", "w-16"]}
          />
        )}
        empty={
          crud.records.length === 0
            ? {
                title: "No Addresses Yet",
                icon: MapPin,
                message:
                  "Track your client's residence history so forms and applications stay accurate.",
                extra: (
                  <AgentReadCard
                    finds="Each address it finds"
                    addLabel="Add Address"
                    onAdd={() => crud.openCreate()}
                  />
                )
              }
            : {
                title: "No Matching Addresses",
                icon: MapPin,
                message: "Try a different search."
              }
        }
      >
        {(searching || !coverage
          ? rows.map((row) => ({ type: "row" as const, row, faded: false }))
          : items
        ).map((item) =>
          item.type === "gap" ? (
            <GapRow
              key={`gap-${item.gap.start.valueOf()}`}
              gap={item.gap}
              columns={COLUMNS.length}
              datesColumn={3}
              kind="address"
            />
          ) : (
            <ClientTableRow
              key={item.row.id}
              domId={item.row.domId}
              highlighted={highlightedId === item.row.domId}
              onActivate={() => crud.openEdit(item.row.entry)}
              className={item.faded ? "opacity-50" : undefined}
            >
              <TitleCell
                icon={MapPin}
                iconClassName={
                  item.row.isCurrent ? "bg-brand-50 text-brand-600" : undefined
                }
                title={item.row.street}
              />
              <MutedCell>{item.row.cityStateZip}</MutedCell>
              <MutedCell>{item.row.country}</MutedCell>
              <DateRangeCell
                start={item.row.entry.start_date}
                end={item.row.entry.end_date}
                isCurrent={item.row.isCurrent}
              />
              <CheckCell checked={item.row.isCurrent} />
              <SourceCell source={item.row.entry.source} />
            </ClientTableRow>
          )
        )}
      </ClientTable>

      <FormSheet
        form={form}
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        title={isEditing ? "Edit address" : "Add address"}
        description="Where the client has lived, with the dates they were there."
        onSubmit={crud.submit}
        isEditing={isEditing}
        isSubmitting={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Delete this address?"
        deleteDescription="The client's current address will be recalculated from the remaining entries."
      >
        <AddressFields<AddressDraft> name="address" required />
        <FormRow>
          <DateField<AddressDraft> name="startDate" label="From" required />
          <DateField<AddressDraft>
            name="endDate"
            label="To"
            required={!isCurrent}
            disabled={isCurrent}
            placeholder={isCurrent ? "Present" : "Pick a date"}
          />
        </FormRow>
        <CheckboxField<AddressDraft>
          name="isCurrent"
          label="This is the client's current address"
          description="Marks this as where the client currently resides and updates their profile address."
        />
        <TextareaField<AddressDraft>
          name="notes"
          label="Notes"
          placeholder="Add any additional notes about this address..."
        />
      </FormSheet>
    </div>
  );
};
